import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import * as yup from 'yup';

import { mapAddressDetailsToString } from '@common/utils/hooks/google/utils';

import {
  Action,
  ActionType,
  useBookingContext,
} from '../../../../_common/context/BookingContext';
import { FetchField } from '../../../../_common/fields/FetchField';
import {
  DataType,
  DefaultValueBehaviour,
  FieldsListQuery_category_fields as Field,
  InputType,
} from '../../../../types/api';
import { fieldsMap } from '../../_common/fieldsMap';

import CustomerFormField from './CustomerFormField';

const alwaysShowInputFieldMap = {
  [InputType.RADIO_BUTTONS]: true,
  [InputType.SWITCH]: true,
};

const alwaysShowValueFieldMap = {
  [InputType.ADDRESS_PICKER]: true,
};

interface Props {
  field: Field;
  validationSchema: yup.ObjectSchema<any>;
}

export default function CustomerField(props: Props) {
  const { field, validationSchema } = props;
  const {
    categoryFieldConfigurationId,
    conditionalActions,
    dataType,
    defaultValueBehaviour,
    icon,
    id,
    inputType,
    label,
    options,
    required,
    requiredForQualification,
    visible,
  } = field;
  const [previousValue, setPreviousValue] = useState<string | boolean | null>(
    null,
  );
  const [{ resetFormInput }, dispatch] = useBookingContext();

  const handleValidate = useCallback(
    (value: string | boolean) => {
      try {
        const trimmedValue = typeof value === 'string' ? value.trim() : value;
        yup.reach(validationSchema, id).validateSync(trimmedValue);
      } catch (error) {
        if (error.name === 'ValidationError') {
          return error.message;
        }
      }
    },
    [validationSchema, id],
  );

  const {
    input: { onBlur, onChange, onFocus, value },
    meta: { error, touched },
  } = useField(id, {
    validate: handleValidate,
    validateFields: [],
    subscription: { touched: true, error: true, value: true },
  });

  useEffect(() => {
    if (!conditionalActions?.some((ca) => ca.condition === value)) {
      setPreviousValue(value);
    }
  }, [value, conditionalActions]);

  const handleChange = useCallback(
    (newValue) => {
      const actions = (conditionalActions ?? [])
        .filter((ca) => ca.condition === newValue)
        .flatMap((ca) => ca.actions)
        .map(({ payload, type }) => ({ type, payload: JSON.parse(payload) }));

      actions.forEach((action) => dispatch(action as unknown as Action));

      // TODO: this is a temporary fix and will only work for the Danske Bank conditional actions
      if (actions.length === 0) {
        onChange(newValue);
      }
    },
    [onChange, conditionalActions, dispatch],
  );

  const FieldComponent =
    dataType === DataType.FETCH_FIELD ? FetchField : fieldsMap[inputType];

  const computedValue = useMemo(() => {
    if (inputType === InputType.DROP_DOWN) {
      const selectedOption = options?.find((option) => option.value === value);

      return selectedOption?.label ?? '';
    }
    if (dataType === DataType.ADDRESS && value) {
      return mapAddressDetailsToString(JSON.parse((value as string) || '{}'));
    }

    return value;
  }, [inputType, dataType, value, options]);

  useEffect(() => {
    if (resetFormInput && resetFormInput === field.key) {
      onChange(previousValue);
      dispatch({ type: ActionType.RESET_FORM_INPUT, payload: null });
    }
  }, [resetFormInput, field, onChange, previousValue, dispatch]);

  if (!FieldComponent || !visible) {
    return null;
  }

  return (
    <CustomerFormField
      alwaysShowInputField={alwaysShowInputFieldMap[inputType]}
      alwaysShowValueField={alwaysShowValueFieldMap[inputType]}
      error={error}
      icon={icon}
      label={dataType !== DataType.BOOLEAN ? label : undefined}
      legend={inputType === InputType.RADIO_BUTTONS ? label : undefined}
      multiline={
        dataType === DataType.ADDRESS || inputType === InputType.TEXT_AREA
      }
      name={inputType === InputType.ADDRESS_PICKER ? undefined : id}
      renderInputField={(
        startEditing,
        stopEditing,
        startHovering,
        stopHovering,
      ) => (
        <FieldComponent
          // @ts-ignore
          categoryFieldConfigurationId={categoryFieldConfigurationId}
          disabled={defaultValueBehaviour === DefaultValueBehaviour.FIXED}
          indent={inputType === InputType.RADIO_BUTTONS}
          label={label}
          name={id}
          onBlur={() => {
            stopEditing();
            onBlur();
          }}
          onChange={handleChange}
          onFocus={() => {
            startEditing();
            onFocus();
          }}
          onMouseEnter={startHovering}
          onMouseLeave={stopHovering}
          options={
            inputType === InputType.RADIO_BUTTONS && options ? options : []
          }
          required={required || false}
          // Hack to get the field to display as disabled on first render, not after focus
          shouldFocus={defaultValueBehaviour === DefaultValueBehaviour.FIXED}
          value={value as any}
        >
          {inputType === InputType.DROP_DOWN &&
            options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
        </FieldComponent>
      )}
      required={required ?? false}
      requiredForQualification={requiredForQualification ?? false}
      touched={touched ?? false}
      value={computedValue}
    />
  );
}
